import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../api/axiosInstance";
import toast from "react-hot-toast";

export const useGetCurrencies = () =>
	useQuery({
		queryKey: ["zoho-currencies"],
		queryFn: async () => {
			const { data } = await api.get("/requests/zoho-book/get-currencies");
			return data?.currencies ?? data?.data?.currencies ?? [];
		},
		staleTime: 5 * 60 * 1000,
	});

export const useGetExchangeRates = (currencyId) =>
	useQuery({
		queryKey: ["zoho-exchange-rates", currencyId],
		queryFn: async () => {
			const { data } = await api.get("/requests/zoho-book/get-exchange-rates", {
				params: { currency_id: currencyId },
			});

			const rates = data?.exchange_rates ?? data?.data?.exchange_rates ?? [];

			return rates.map((rate) => ({
				...rate,
				user_rate: rate.user_rate || (rate.rate ? 1 / rate.rate : null),
				zoho_rate: rate.rate,
			}));
		},
		enabled: !!currencyId,
		staleTime: 30_000,
	});

export const useGetExchangeRate = (currencyId, exchangeRateId) =>
	useQuery({
		queryKey: ["zoho-exchange-rate", currencyId, exchangeRateId],
		queryFn: async () => {
			const { data } = await api.get("/requests/zoho-book/get-exchange-rate", {
				params: { currency_id: currencyId, exchange_rate_id: exchangeRateId },
			});
			return data?.exchange_rate ?? data?.data?.exchange_rate ?? null;
		},
		enabled: !!(currencyId && exchangeRateId),
		staleTime: 30_000,
	});

export const useCreateExchangeRate = () => {
	const qc = useQueryClient();

	return useMutation({
		mutationFn: async ({ currencyId, userRate, zohoRate, effectiveDate }) => {
			const { data } = await api.post(
				"/requests/zoho-book/create-exchange-rate",
				{ effective_date: effectiveDate, rate: Number(zohoRate) },
				{ params: { currency_id: currencyId } }
			);

			return {
				...data,
				data: {
					...data.data,
					exchange_rate: {
						...data.data?.exchange_rate,
						user_rate: Number(userRate),
						zoho_rate: Number(zohoRate),
					},
				},
			};
		},

		onSuccess: (data, { currencyId }) => {
			if (data?.message?.toLowerCase().includes("access denied")) {
				toast.error(data.message);
				return;
			}

			toast.success(data?.message || "Exchange rate created");

			qc.invalidateQueries({ queryKey: ["zoho-currencies"] });
			qc.invalidateQueries({ queryKey: ["zoho-exchange-rates", currencyId] });
		},

		onError: (error) => {
			toast.error(
				error?.response?.data?.message || "Failed to create exchange rate"
			);
		},
	});
};

export const useUpdateExchangeRate = () => {
	const qc = useQueryClient();

	return useMutation({
		mutationFn: async ({
			currencyId,
			exchangeRateId,
			userRate,
			zohoRate,
			effectiveDate,
		}) => {
			const { data } = await api.put(
				"/requests/zoho-book/update-exchange-rate",
				{ effective_date: effectiveDate, rate: Number(zohoRate) },
				{
					params: { currency_id: currencyId, exchange_rate_id: exchangeRateId },
				}
			);

			return {
				...data,
				data: {
					...data.data,
					exchange_rate: {
						...data.data?.exchange_rate,
						user_rate: Number(userRate),
						zoho_rate: Number(zohoRate),
					},
				},
			};
		},

		onSuccess: (data, { currencyId }) => {
			if (data?.message?.toLowerCase().includes("access denied")) {
				toast.error(data.message);
				return;
			}

			toast.success(data?.message || "Exchange rate updated");

			qc.invalidateQueries({ queryKey: ["zoho-currencies"] });
			qc.invalidateQueries({ queryKey: ["zoho-exchange-rates", currencyId] });
		},

		onError: (error) => {
			toast.error(
				error?.response?.data?.message || "Failed to update exchange rate"
			);
		},
	});
};

export const useDeleteExchangeRate = () => {
	const qc = useQueryClient();

	return useMutation({
		mutationFn: async ({ currencyId, exchangeRateId }) => {
			const { data } = await api.delete(
				"/requests/zoho-book/delete-exchange-rate",
				{
					params: { currency_id: currencyId, exchange_rate_id: exchangeRateId },
				}
			);
			return data;
		},

		onSuccess: (data, { currencyId }) => {
			if (data?.message?.toLowerCase().includes("access denied")) {
				toast.error(data.message);
				return;
			}

			toast.success(data?.message || "Exchange rate deleted");

			qc.invalidateQueries({ queryKey: ["zoho-currencies"] });
			qc.invalidateQueries({ queryKey: ["zoho-exchange-rates", currencyId] });
		},

		onError: (error) => {
			toast.error(
				error?.response?.data?.message || "Failed to delete exchange rate"
			);
		},
	});
};
